import { addDoc, collection, doc, getDoc, getDocs, limit, orderBy, query, serverTimestamp, setDoc, updateDoc, where } from 'firebase/firestore';
import { getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { auth, db, storage } from "../firebase";
import { isFieldOpsMode } from './businessMode';

const ACTIVE_SHIFT_KEY = 'neoSecurityActiveShift';

export function getResolvedOrgId() {
  try {
    const params = JSON.parse(localStorage.getItem('installParams') || 'null');
    return (
      params?.orgId ||
      localStorage.getItem('orgId') ||
      localStorage.getItem('linkedOrgId') ||
      ''
    );
  } catch {
    return localStorage.getItem('orgId') || localStorage.getItem('linkedOrgId') || '';
  }
}

export function getResolvedSiteId() {
  try {
    const params = JSON.parse(localStorage.getItem('installParams') || 'null');
    return (
      localStorage.getItem('securitySiteId') ||
      params?.siteId ||
      params?.storeId ||
      localStorage.getItem('storeId') ||
      localStorage.getItem('linkedStoreId') ||
      ''
    );
  } catch {
    return localStorage.getItem('securitySiteId') || localStorage.getItem('storeId') || localStorage.getItem('linkedStoreId') || '';
  }
}

export function getResolvedOrgName() {
  try {
    const params = JSON.parse(localStorage.getItem('installParams') || 'null');
    return (
      params?.orgName ||
      localStorage.getItem('orgName') ||
      localStorage.getItem('linkedOrgName') ||
      ''
    );
  } catch {
    return localStorage.getItem('orgName') || localStorage.getItem('linkedOrgName') || '';
  }
}

export function getResolvedSiteName() {
  try {
    const params = JSON.parse(localStorage.getItem('installParams') || 'null');
    return (
      params?.siteName ||
      params?.storeName ||
      localStorage.getItem('siteName') ||
      localStorage.getItem('storeName') ||
      localStorage.getItem('linkedStoreName') ||
      ''
    );
  } catch {
    return localStorage.getItem('siteName') || localStorage.getItem('storeName') || localStorage.getItem('linkedStoreName') || '';
  }
}

export function getDeviceId() {
  return (
    localStorage.getItem('securityDeviceId') ||
    localStorage.getItem('deviceUid') ||
    auth.currentUser?.uid ||
    'unknown-device'
  );
}

export async function ensureFieldAuth() {
  const user = auth.currentUser;
  if (!user) {
    throw new Error("Device auth not ready. Please relink this device.");
  }
  return user;
}

export async function getSecurityProfile(orgId, uid) {
  if (!orgId || !uid) return null;
  const candidates = [
    ['orgs', orgId, 'securityUsers', uid],
    ['orgs', orgId, 'members', uid],
    ['orgs', orgId, 'fieldUsers', uid],
  ];

  for (const path of candidates) {
    try {
      const snap = await getDoc(doc(db, ...path));
      if (snap.exists()) return { id: snap.id, ...snap.data() };
    } catch {
      // ignore and continue
    }
  }
  return null;
}

export async function getAssignedSites(orgId, profile) {
  if (!orgId) return [];

  const assignedIds = [
    ...(Array.isArray(profile?.assignedSiteIds) ? profile.assignedSiteIds : []),
    ...(Array.isArray(profile?.siteIds) ? profile.siteIds : []),
    ...(profile?.siteId ? [profile.siteId] : []),
    ...(profile?.storeId ? [profile.storeId] : []),
    ...(getResolvedSiteId() ? [getResolvedSiteId()] : []),
  ].filter(Boolean);

  const uniqueIds = [...new Set(assignedIds.map(String))];

  const hydrateSite = async (siteId) => {
    const paths = [
      ['orgs', orgId, 'sites', siteId],
      ['orgs', orgId, 'cashup_stores', siteId],
      ['orgs', orgId, 'stores', siteId],
    ];

    for (const path of paths) {
      try {
        const snap = await getDoc(doc(db, ...path));
        if (snap.exists()) {
          const data = snap.data() || {};
          return {
            id: siteId,
            name: data.name || data.siteName || data.storeName || data.label || 'Unnamed site',
            address: data.address || data.location || '',
            lat: toNumber(data.lat ?? data.latitude),
            lng: toNumber(data.lng ?? data.longitude),
            allowedRadiusMeters: toNumber(data.allowedRadiusMeters ?? data.radiusMeters ?? data.siteRadiusMeters) || 120,
            raw: data,
          };
        }
      } catch {
        // continue
      }
    }
    return null;
  };

  if (uniqueIds.length) {
    const hydrated = await Promise.all(uniqueIds.map(hydrateSite));
    return hydrated.filter(Boolean);
  }

  const collectionCandidates = [
    collection(db, 'orgs', orgId, 'sites'),
    collection(db, 'orgs', orgId, 'cashup_stores'),
  ];

  for (const col of collectionCandidates) {
    try {
      const snap = await getDocs(query(col, limit(20)));
      if (!snap.empty) {
        return snap.docs.map((d) => {
          const data = d.data() || {};
          return {
            id: d.id,
            name: data.name || data.siteName || data.storeName || data.label || 'Unnamed site',
            address: data.address || data.location || '',
            lat: toNumber(data.lat ?? data.latitude),
            lng: toNumber(data.lng ?? data.longitude),
            allowedRadiusMeters: toNumber(data.allowedRadiusMeters ?? data.radiusMeters ?? data.siteRadiusMeters) || 120,
            raw: data,
          };
        });
      }
    } catch {
      // continue
    }
  }

  return [];
}

export async function getCheckpoints(orgId, siteId) {
  if (!orgId || !siteId) return [];
  const candidates = [
    collection(db, 'orgs', orgId, 'sites', siteId, 'checkpoints'),
    collection(db, 'orgs', orgId, 'sites', siteId, 'security_checkpoints'),
    collection(db, 'orgs', orgId, 'cashup_stores', siteId, 'checkpoints'),
  ];

  for (const col of candidates) {
    try {
      const snap = await getDocs(query(col, limit(100)));
      if (!snap.empty) {
        return snap.docs.map((d) => normalizeCheckpoint(d.id, d.data()));
      }
    } catch {
      // continue
    }
  }

  return [];
}

function normalizeCheckpoint(id, data = {}) {
  return {
    id,
    name: data.name || data.label || 'Checkpoint',
    qrToken: String(data.qrToken || data.token || data.code || id),
    lat: toNumber(data.lat ?? data.latitude),
    lng: toNumber(data.lng ?? data.longitude),
    radiusMeters: toNumber(data.radiusMeters ?? data.allowedRadiusMeters) || 50,
    sequence: toNumber(data.sequence ?? data.order ?? data.position) || 0,
    routeId: data.routeId || data.route || 'default-route',
    routeName: data.routeName || data.route || 'General Patrol',
    routeDescription: data.routeDescription || '',
    description: data.description || data.locationLabel || data.notes || '',
    active: data.active !== false,
    raw: data,
  };
}

export function matchCheckpoint(checkpoints, code) {
  const needle = String(code || '').trim().toLowerCase();
  if (!needle) return null;
  return checkpoints.find((cp) => {
    const parts = [cp.id, cp.qrToken, cp.name]
      .filter(Boolean)
      .map((v) => String(v).trim().toLowerCase());
    return parts.includes(needle);
  }) || null;
}

export function normalizeCheckpointCode(code) {
  return String(code || '').trim().toLowerCase();
}

export function doesCheckpointCodeMatch(checkpoint, code) {
  const needle = normalizeCheckpointCode(code);
  if (!needle) return false;
  return [checkpoint?.id, checkpoint?.qrToken, checkpoint?.name]
    .filter(Boolean)
    .map(normalizeCheckpointCode)
    .includes(needle);
}

export function getCheckpointStatusLabel(status, flagReason = '') {
  const safeStatus = String(status || '').toLowerCase();
  const safeReason = String(flagReason || '').toLowerCase();

  if (safeStatus === 'valid') return 'Verified';
  if (safeStatus === 'skipped') return 'Skipped';
  if (safeStatus === 'invalid') return 'Invalid';
  if (safeStatus === 'flagged' && safeReason === 'duplicate_scan_too_fast') return 'Too fast';
  if (safeStatus === 'flagged' && safeReason === 'outside_checkpoint_radius') return 'Far from point';
  if (safeStatus === 'flagged' && safeReason === 'out_of_sequence_scan') return 'Out of order';
  if (safeStatus === 'flagged' && safeReason === 'wrong_checkpoint_token') return 'Wrong point';
  if (safeStatus === 'flagged') return 'Review';
  if (safeStatus) return safeStatus;
  return 'Pending';
}

export function getPatrolHealth(logs = [], route = null) {
  const list = Array.isArray(logs) ? logs : [];
  const routeCheckpoints = Array.isArray(route?.checkpoints) ? route.checkpoints : [];
  const latest = list[0] || null;
  const lastScannedAt = latest?.scannedAtClient || latest?.scannedAt?.toMillis?.() || latest?.scannedAt?.seconds * 1000 || null;
  const now = Date.now();
  const minsSince = lastScannedAt ? Math.max(0, Math.round((now - lastScannedAt) / 60000)) : null;

  const missed = routeCheckpoints.filter((cp) => {
    const found = list.find((row) => String(row.checkpointId || '') === String(cp.id));
    return !found;
  }).length;

  if (!latest) {
    return {
      title: 'On track',
      copy: 'No checkpoint logged on this shift yet.',
      tone: 'neutral',
      minsSince: null,
      missed,
    };
  }

  if (latest.status === 'invalid') {
    return {
      title: 'Review needed',
      copy: 'Latest checkpoint was marked invalid and needs manager review.',
      tone: 'danger',
      minsSince,
      missed,
    };
  }

  if (latest.status === 'flagged') {
    return {
      title: 'Attention',
      copy: latest.flagReason ? latest.flagReason.replace(/_/g, ' ') : 'Latest checkpoint needs review.',
      tone: 'warning',
      minsSince,
      missed,
    };
  }

  if (minsSince != null && minsSince >= 30) {
    return {
      title: 'Patrol gap',
      copy: `${minsSince} min since last checkpoint. Check whether the route paused or a point was missed.`,
      tone: 'warning',
      minsSince,
      missed,
    };
  }

  return {
    title: 'On track',
    copy: minsSince == null ? 'Patrol is running.' : `Last checkpoint logged ${minsSince} min ago.`,
    tone: 'good',
    minsSince,
    missed,
  };
}

export function readActiveShift() {
  try {
    return JSON.parse(localStorage.getItem(ACTIVE_SHIFT_KEY) || 'null');
  } catch {
    return null;
  }
}

export function storeActiveShift(shift) {
  try {
    if (!shift) localStorage.removeItem(ACTIVE_SHIFT_KEY);
    else localStorage.setItem(ACTIVE_SHIFT_KEY, JSON.stringify(shift));
  } catch {
    // ignore
  }
}

export async function findLatestActiveShift(orgId, uid) {
  if (!orgId || !uid) return null;
  try {
    const q = query(
      collection(db, 'orgs', orgId, 'securityShifts'),
      where('uid', '==', uid),
      where('status', '==', 'active'),
      orderBy('startedAt', 'desc'),
      limit(1)
    );
    const snap = await getDocs(q);
    if (snap.empty) return null;
    const d = snap.docs[0];
    const data = d.data() || {};
    const shift = { id: d.id, ...data };
    storeActiveShift(shift);
    return shift;
  } catch {
    return readActiveShift();
  }
}

export async function startSecurityShift({ orgId, site, profile, coords }) {
  const user = await ensureFieldAuth();
  const deviceId = getDeviceId();
  const distance = site?.lat && site?.lng && coords ? haversineMeters(coords, { lat: site.lat, lng: site.lng }) : null;
  const radius = toNumber(site?.allowedRadiusMeters) || 120;
  const status = distance == null ? 'flagged' : distance <= radius ? 'active' : distance <= Math.max(radius * 2, 200) ? 'flagged' : 'blocked';

  const payload = {
    uid: user.uid,
    userEmail: user.email || profile?.email || '',
    userName: profile?.fullName || profile?.name || profile?.displayName || user.displayName || '',
    siteId: site?.id || '',
    siteName: site?.name || '',
    siteRadiusMeters: radius,
    startedAt: serverTimestamp(),
    startedAtClient: Date.now(),
    startLat: toNumber(coords?.lat),
    startLng: toNumber(coords?.lng),
    deviceId,
    businessMode: localStorage.getItem('businessMode') || '',
    status,
    flagReason: status === 'flagged' ? 'start_outside_radius' : status === 'blocked' ? 'too_far_from_site' : '',
  };

  const refDoc = await addDoc(collection(db, 'orgs', orgId, 'securityShifts'), payload);
  const shift = { id: refDoc.id, ...payload, startedAt: payload.startedAtClient };
  storeActiveShift(shift);
  localStorage.setItem('securitySiteId', site?.id || '');
  return { shift, status, distance };
}

export async function endSecurityShift({ orgId, shiftId, coords }) {
  if (!orgId || !shiftId) throw new Error('Missing active shift.');
  const refDoc = doc(db, 'orgs', orgId, 'securityShifts', shiftId);
  await updateDoc(refDoc, {
    endedAt: serverTimestamp(),
    endedAtClient: Date.now(),
    endLat: toNumber(coords?.lat),
    endLng: toNumber(coords?.lng),
    status: 'completed',
  });
  storeActiveShift(null);
}

export async function savePatrolScan({
  orgId,
  siteId,
  checkpoint,
  shiftId,
  coords,
  previousScanAt,
  statusOverride,
  flagReason: flagReasonOverride,
  scannedCode,
  routeId,
  routeName,
  expectedCheckpointId,
  previousCheckpointId,
}) {
  const user = await ensureFieldAuth();
  const distance = checkpoint?.lat && checkpoint?.lng && coords ? haversineMeters(coords, { lat: checkpoint.lat, lng: checkpoint.lng }) : null;
  const validRadius = toNumber(checkpoint?.radiusMeters) || 50;
  let status = String(statusOverride || 'valid').toLowerCase();
  let flagReason = String(flagReasonOverride || '');

  if (status !== 'skipped') {
    if (!doesCheckpointCodeMatch(checkpoint, scannedCode)) {
      status = 'flagged';
      flagReason = 'wrong_checkpoint_token';
    }

    if (distance == null && status === 'valid') {
      status = 'flagged';
      flagReason = 'no_live_location';
    } else if (distance > validRadius && distance <= Math.max(validRadius * 3, 150) && status === 'valid') {
      status = 'flagged';
      flagReason = 'outside_checkpoint_radius';
    } else if (distance > Math.max(validRadius * 3, 150)) {
      status = 'invalid';
      flagReason = 'too_far_from_checkpoint';
    }

    if (expectedCheckpointId && checkpoint?.id && String(expectedCheckpointId) !== String(checkpoint.id) && status === 'valid') {
      status = 'flagged';
      flagReason = 'out_of_sequence_scan';
    }

    if (previousScanAt && Date.now() - Number(previousScanAt) < 60_000 && status === 'valid') {
      status = 'flagged';
      flagReason = 'duplicate_scan_too_fast';
    }
  }

  const payload = {
    uid: user.uid,
    siteId,
    shiftId: shiftId || '',
    checkpointId: checkpoint?.id || '',
    checkpointName: checkpoint?.name || '',
    checkpointOrder: toNumber(checkpoint?.order ?? checkpoint?.sequence) || 0,
    qrToken: checkpoint?.qrToken || '',
    scannedCode: String(scannedCode || checkpoint?.qrToken || checkpoint?.id || ''),
    scannedAt: serverTimestamp(),
    scannedAtClient: Date.now(),
    lat: toNumber(coords?.lat),
    lng: toNumber(coords?.lng),
    distanceFromCheckpoint: distance == null ? null : Math.round(distance),
    status,
    flagReason,
    routeId: routeId || checkpoint?.routeId || '',
    routeName: routeName || checkpoint?.routeName || '',
    expectedCheckpointId: expectedCheckpointId || '',
    previousCheckpointId: previousCheckpointId || '',
    deviceId: getDeviceId(),
  };

  const refDoc = await addDoc(collection(db, 'orgs', orgId, 'patrolScans'), payload);
  localStorage.setItem('neoSecurityLastPatrolAt', String(payload.scannedAtClient));
  return { id: refDoc.id, ...payload };
}

export async function createIncident({ orgId, siteId, shiftId, payload, files = [] }) {
  const user = await ensureFieldAuth();
  const refDoc = doc(collection(db, 'orgs', orgId, 'incidents'));
  const urls = [];

  for (const file of files) {
    const cleanName = `${Date.now()}-${String(file.name || 'image').replace(/[^a-zA-Z0-9._-]/g, '-')}`;
    const fileRef = ref(storage, `orgs/${orgId}/incidents/${refDoc.id}/${cleanName}`);
    await uploadBytes(fileRef, file);
    urls.push(await getDownloadURL(fileRef));
  }

  await setDoc(refDoc, {
    uid: user.uid,
    siteId: siteId || '',
    shiftId: shiftId || '',
    type: payload.type || 'general',
    severity: payload.severity || 'medium',
    notes: payload.notes || '',
    title: payload.title || '',
    lat: toNumber(payload.lat),
    lng: toNumber(payload.lng),
    photoUrls: urls,
    createdAt: serverTimestamp(),
    createdAtClient: Date.now(),
    status: 'open',
    deviceId: getDeviceId(),
  });

  return { id: refDoc.id, photoUrls: urls };
}

export async function getRecentSecurityShifts(orgId, uid, maxRows = 5) {
  if (!orgId || !uid) return [];
  try {
    const q = query(
      collection(db, 'orgs', orgId, 'securityShifts'),
      where('uid', '==', uid),
      orderBy('startedAt', 'desc'),
      limit(maxRows)
    );
    const snap = await getDocs(q);
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  } catch {
    return [];
  }
}

export async function getRecentPatrolScans(orgId, uid, maxRows = 5) {
  if (!orgId || !uid) return [];
  try {
    const q = query(
      collection(db, 'orgs', orgId, 'patrolScans'),
      where('uid', '==', uid),
      orderBy('scannedAt', 'desc'),
      limit(maxRows)
    );
    const snap = await getDocs(q);
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  } catch {
    return [];
  }
}

export async function getRecentIncidents(orgId, uid, maxRows = 5) {
  if (!orgId || !uid) return [];
  try {
    const q = query(
      collection(db, 'orgs', orgId, 'incidents'),
      where('uid', '==', uid),
      orderBy('createdAt', 'desc'),
      limit(maxRows)
    );
    const snap = await getDocs(q);
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  } catch {
    return [];
  }
}

export function formatDateTime(value) {
  const ts = value?.toDate ? value.toDate() : value ? new Date(value) : null;
  if (!ts || Number.isNaN(ts.getTime())) return '—';
  return ts.toLocaleString();
}

export function haversineMeters(a, b) {
  const lat1 = toNumber(a?.lat);
  const lon1 = toNumber(a?.lng);
  const lat2 = toNumber(b?.lat);
  const lon2 = toNumber(b?.lng);
  if ([lat1, lon1, lat2, lon2].some((v) => Number.isNaN(v) || v == null)) return null;
  const R = 6371000;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const p1 = (lat1 * Math.PI) / 180;
  const p2 = (lat2 * Math.PI) / 180;
  const sinDLat = Math.sin(dLat / 2);
  const sinDLon = Math.sin(dLon / 2);
  const x = sinDLat * sinDLat + Math.cos(p1) * Math.cos(p2) * sinDLon * sinDLon;
  return 2 * R * Math.atan2(Math.sqrt(x), Math.sqrt(1 - x));
}

export async function getCurrentCoords(options = {}) {
  if (!navigator.geolocation) throw new Error('Geolocation is not supported on this device.');
  return new Promise((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(
      (position) => resolve({
        lat: position.coords.latitude,
        lng: position.coords.longitude,
        accuracy: position.coords.accuracy,
      }),
      (err) => reject(new Error(getGeoErrorMessage(err))),
      {
        enableHighAccuracy: true,
        timeout: 20000,
        maximumAge: 5000,
        ...options,
      }
    );
  });
}

export async function getPermissionState(name) {
  if (!navigator.permissions?.query) return 'prompt';
  try {
    const res = await navigator.permissions.query({ name });
    return res.state || 'prompt';
  } catch {
    return 'prompt';
  }
}

export function getGeoErrorMessage(err) {
  switch (err?.code) {
    case 1:
      return 'Location permission was denied.';
    case 2:
      return 'Could not get a stable GPS location.';
    case 3:
      return 'Location request timed out.';
    default:
      return err?.message || 'Location failed.';
  }
}

export function summarizeShiftStatus(status) {
  if (status === 'active') return 'Valid start';
  if (status === 'flagged') return 'Started with warning';
  if (status === 'blocked') return 'Too far from site';
  return 'Unknown';
}

export function detectBusinessModeAllowed(mode) {
  return isFieldOpsMode(mode);
}

function toNumber(value) {
  if (value == null || value === '') return null;
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
}
